import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLevelAccess } from '../../context/LevelAccessContext';
import { useToast } from '../../context/ToastContext';
import { Card, Badge, ProgressBar, SectionHeader, Spinner } from '../../components/shared/UI';
import API from '../../utils/apiClient';

const statusMeta = {
  not_started: { label: 'Not Started', color: 'bg-gray-100 text-gray-500', icon: '○' },
  in_progress:  { label: 'In Progress',  color: 'bg-gold-100 text-de-black border border-gold-400', icon: '◑' },
  complete:     { label: 'Complete',     color: 'bg-green-100 text-green-800 border border-green-300', icon: '✓' },
};

export default function StudentCurriculumPage() {
  const { user } = useAuth();
  const { isLevelUnlocked } = useLevelAccess();
  const { addToast } = useToast();

  const [grouped, setGrouped] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const studentId = user?.studentId || user?.student?.id;
    if (!studentId) return;
    API.get(`/curriculum/student/${studentId}`)
      .then(({ data }) => setGrouped(data.grouped || {}))
      .catch(() => addToast('Failed to load curriculum','error'))
      .finally(() => setLoading(false));
  }, [user]);

  const levels = ['A1','A2','B1','B2'];

  if (loading) return (
    <div className="flex items-center justify-center py-24"><Spinner size="lg" className="text-brand-500" /></div>
  );

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <SectionHeader
        title="My Curriculum"
        subtitle="Your learning roadmap — complete topics with your teacher to track progress"
      />

      {levels.map(level => {
        const topics   = grouped[level] || [];
        const unlocked = isLevelUnlocked(level);
        if (!unlocked && topics.length === 0) return null;

        const completed  = topics.filter(t => t.status === 'complete').length;
        const pct        = topics.length > 0 ? Math.round((completed / topics.length) * 100) : 0;

        return (
          <div key={level} className="mb-8">
            {/* Level header */}
            <div className={`flex items-center justify-between mb-3 p-4 rounded-xl border-l-4
              ${unlocked ? 'border-brand-500 bg-surface' : 'border-gray-200 bg-gray-50 opacity-60'}`}>
              <div className="flex items-center gap-3">
                <span className="text-2xl font-bold text-de-black font-display">{level}</span>
                {!unlocked && <Badge variant="locked">🔒 Locked</Badge>}
                {unlocked && <span className="text-sm text-gray-500">{topics.length} topics</span>}
              </div>
              {unlocked && topics.length > 0 && (
                <div className="w-40">
                  <ProgressBar value={completed} max={topics.length} color="red" label={`${completed}/${topics.length} complete`} />
                </div>
              )}
            </div>

            {/* Topics grid */}
            {unlocked && topics.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {topics.map(topic => {
                  const meta = statusMeta[topic.status] || statusMeta.not_started;
                  return (
                    <Card key={topic.id} className="p-4" accent={topic.status === 'complete' ? 'red' : undefined}>
                      <div className="flex items-start justify-between mb-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${meta.color}`}>
                          {meta.icon} {meta.label}
                        </span>
                        <span className="text-xs text-gray-400">#{topic.order_index}</span>
                      </div>
                      <h4 className="text-sm font-bold text-de-black mb-1">{topic.title}</h4>
                      {topic.description && (
                        <p className="text-xs text-gray-500 leading-snug mb-3">{topic.description}</p>
                      )}
                      {topic.status === 'complete' && (
                        <div className="text-xs text-green-600 font-semibold">✓ Topic completed</div>
                      )}
                      {topic.status === 'in_progress' && (
                        <div className="text-xs text-gold-600 font-medium">In progress — ask your teacher to mark complete</div>
                      )}
                      {topic.status === 'not_started' && (
                        <div className="text-xs text-gray-400">Not started yet</div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
            {unlocked && topics.length === 0 && (
              <p className="text-sm text-gray-400 pl-4">No topics added yet for this level.</p>
            )}
          </div>
        );
      })}
    </div>
  );
}
